import React, { ReactNode } from 'react';
import {
    Box,
    FormControl,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { CityIcon } from 'src/icons/CityIcon';
import { CompanyIcon } from 'src/icons/CompanyIcon';
import { EmailIcon } from 'src/icons/EmailIcon';
import { NameIcon } from 'src/icons/NameIcon';
import { VatIcon } from 'src/icons/VatIcon';
import { WebsiteIcon } from 'src/icons/WebsiteIcon';
import { MuiTelInput, MuiTelInputCountry } from 'mui-tel-input';
import { GetProfileSettingsResponse, ProfileImageType } from 'src/model/api/v2/data-contracts';
import { SettingsSection } from 'src/pages/ProjectSettingsPage/components/SettingsSection';
import { BasicDetailsIcon } from 'src/icons/BasicDetailsIcon';
import { SettingsSelect } from 'src/pages/ProjectSettingsPage/components/SettingsSelect';
import { ProfileImageUpload } from 'src/pages/ProfileSettingsPage/components/ProfileImageUpload';
import { ProfileForm, ProfileFormErrors, ProfileFormField } from 'src/pages/ProfileSettingsPage/useProfileSettingsForm';
import { countries } from 'src/utils/countries';
import { isVatCountry, upperCaseVatNumberInput, vatNumberExamples } from 'src/utils/vat';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';

type TextFieldName = Exclude<ProfileFormField, 'preferred_currency_id' | 'planner_icon_set_id' | 'country' | 'site_language' | 'telephone'>;

// same order as the legacy phone input
const PREFERRED_PHONE_COUNTRIES: MuiTelInputCountry[] = ['HU', 'RO', 'AT', 'DE', 'GB'];
const COUNTRY_MENU_MAX_HEIGHT = 250;
// the input icons are shown in their design size
const LINE_ICON_SIZE = 16;
const NAME_ICON_SIZE = 11;
const VAT_ICON_SIZE = 13;
// same as the placeholder opacity of the MUI inputs
const PLACEHOLDER_OPACITY = 0.42;

type TextFieldOptions = {
    required?: boolean;
    helperText?: string;
    icon?: ReactNode;
};

type Props = {
    form: ProfileForm;
    formValidationErrors: ProfileFormErrors;
    options: GetProfileSettingsResponse['options'];
    avatarFileName: string;
    showBillingNotice: boolean;
    onFieldChange: <F extends ProfileFormField>(field: F, value: ProfileForm[F]) => void;
    onFieldBlur: () => void;
    onFieldChangeAndSave: <F extends ProfileFormField>(field: F, value: ProfileForm[F]) => void;
};

export const BasicDetailsSection = ({
    form,
    formValidationErrors,
    options,
    avatarFileName,
    showBillingNotice,
    onFieldChange,
    onFieldBlur,
    onFieldChangeAndSave,
}: Props) => {
    const renderTextField = (field: TextFieldName, label: string, { required = false, helperText, icon }: TextFieldOptions = {}) => (
        <TextField
            id={`profile-${field}`}
            label={icon ? undefined : label}
            placeholder={icon ? label : undefined}
            value={form[field]}
            required={required}
            error={!!formValidationErrors[field]}
            helperText={formValidationErrors[field] ?? helperText}
            fullWidth
            size="small"
            slotProps={{
                inputLabel: { shrink: true },
                htmlInput: icon ? { 'aria-label': label } : undefined,
                input: icon ? {
                    startAdornment: (
                        <InputAdornment position="start" sx={{ color: theme.palette.grey[500] }}>
                            {icon}
                        </InputAdornment>
                    ),
                } : undefined,
            }}
            onChange={(event) => {
                if (field === 'tax_number') upperCaseVatNumberInput(event.target);
                onFieldChange(field, event.target.value);
            }}
            onBlur={onFieldBlur}
            onKeyDown={(event) => {
                if (event.key === 'Enter') onFieldBlur();
            }}
        />
    );

    return (
        <SettingsSection
            title={ts('edit_profile', 'Edit profile')}
            titleIcon={<BasicDetailsIcon />}
            titleAction={
                <Typography component="span" variant="body2" color={theme.palette.text.secondary}>
                    {ts('profile_settings_basic_details', 'Basic details')}
                </Typography>
            }
        >
            <Stack spacing={3}>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                    {ts('profile_settings_change_picture', 'Change profile picture')}
                </Typography>
                <ProfileImageUpload type={ProfileImageType.Avatar} fileName={avatarFileName} />
                <Typography variant="body2" color={theme.palette.text.secondary}>
                    {ts('profile_settings_personal_details', 'Personal details')}
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Stack spacing={3}>
                            {renderTextField('first_name', ts('profile_settings_first_name', 'First Name'), {
                                required: true,
                                icon: <NameIcon sx={{ fontSize: NAME_ICON_SIZE }} />,
                            })}
                            {renderTextField('last_name', ts('profile_settings_last_name', 'Last Name'), {
                                required: true,
                                icon: <NameIcon sx={{ fontSize: NAME_ICON_SIZE }} />,
                            })}
                            {renderTextField('website', ts('profile_settings_website', 'Website'), {
                                icon: <WebsiteIcon sx={{ fontSize: LINE_ICON_SIZE }} />,
                            })}
                            {renderTextField('email', ts('profile_settings_email', 'Email address'), {
                                required: true,
                                icon: <EmailIcon sx={{ fontSize: LINE_ICON_SIZE }} />,
                            })}
                            {renderTextField('company', ts('company', 'Company'), {
                                icon: <CompanyIcon sx={{ fontSize: LINE_ICON_SIZE }} />,
                            })}
                            {isVatCountry(form.country) && renderTextField('tax_number', ts('vat_number', 'VAT number'), {
                                helperText: vatNumberExamples(),
                                icon: <VatIcon sx={{ fontSize: VAT_ICON_SIZE }} />,
                            })}
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Stack spacing={3}>
                            <FormControl fullWidth size="small" error={!!formValidationErrors.country}>
                                <Select
                                    id="profile-country"
                                    displayEmpty
                                    renderValue={(country) => country || (
                                        <Box component="span" sx={{ opacity: PLACEHOLDER_OPACITY }}>
                                            {ts('country', 'Country')}
                                        </Box>
                                    )}
                                    SelectDisplayProps={{ 'aria-label': ts('country', 'Country') }}
                                    value={form.country}
                                    MenuProps={{ slotProps: { paper: { sx: { maxHeight: COUNTRY_MENU_MAX_HEIGHT } } } }}
                                    onChange={(event) => onFieldChangeAndSave('country', event.target.value)}
                                >
                                    {countries.map(country => (
                                        <MenuItem key={country} value={country}>{country}</MenuItem>
                                    ))}
                                </Select>
                                {formValidationErrors.country && <FormHelperText>{formValidationErrors.country}</FormHelperText>}
                            </FormControl>
                            {renderTextField('city', ts('city', 'City'), {
                                icon: <CityIcon sx={{ fontSize: LINE_ICON_SIZE }} />,
                            })}
                            {renderTextField('street_address', ts('street_address', 'Street address'), {
                                icon: <NameIcon sx={{ fontSize: NAME_ICON_SIZE }} />,
                            })}
                            {renderTextField('zipcode', ts('zip_code', 'Zip code'), {
                                icon: <EmailIcon sx={{ fontSize: LINE_ICON_SIZE }} />,
                            })}
                            <MuiTelInput
                                id="profile-telephone"
                                placeholder={ts('profile_settings_telephone', 'Telephone')}
                                value={form.telephone}
                                preferredCountries={PREFERRED_PHONE_COUNTRIES}
                                MenuProps={{ slotProps: { paper: { sx: { maxHeight: COUNTRY_MENU_MAX_HEIGHT } } } }}
                                error={!!formValidationErrors.telephone}
                                helperText={formValidationErrors.telephone}
                                fullWidth
                                size="small"
                                slotProps={{ htmlInput: { 'aria-label': ts('profile_settings_telephone', 'Telephone') } }}
                                onChange={(value) => onFieldChange('telephone', value)}
                                onBlur={onFieldBlur}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') onFieldBlur();
                                }}
                            />
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="profile-site-language-label" shrink>
                                {ts('profile_settings_site_language', 'Site language')}
                            </InputLabel>
                            <Select
                                labelId="profile-site-language-label"
                                id="profile-site-language"
                                label={ts('profile_settings_site_language', 'Site language')}
                                notched
                                value={form.site_language}
                                onChange={(event) => onFieldChangeAndSave('site_language', event.target.value)}
                            >
                                {options.site_languages.map(language => (
                                    <MenuItem key={language.code} value={language.code}>{language.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <SettingsSelect
                            id="profile-preferred-currency"
                            label={ts('profile_settings_preferred_currency', 'Preferred currency')}
                            value={form.preferred_currency_id}
                            options={options.currencies}
                            onChange={(currencyId) => onFieldChangeAndSave('preferred_currency_id', currencyId)}
                        />
                    </Grid>
                    {showBillingNotice && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="body2" color={theme.palette.warning.main}>
                                {ts('billing_data_required_text', 'In order to purchase pro license or other features, please fill out your country, city, zipcode, street address.')}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Stack>
        </SettingsSection>
    );
};
